import React, { useState, useEffect } from 'react';
import { dealsApi } from '../services/api';
import { can } from '../lib/permissions';
import {
  Search,
  Settings,
  Plus,
  LayoutGrid,
  List,
  ChevronDown,
  X,
  DollarSign,
  Calendar,
  Clock,
  Building2,
  Archive,
  XCircle,
  Edit2,
  Check,
  Trash2,
  User
} from 'lucide-react';
import './Pipeline.css';

const DEFAULT_STAGES = [
  { id: 'ioi',                   name: 'IOI',                     color: '#6B7280' },
  { id: 'loi',                   name: 'LOI',                     color: '#F59E0B' },
  { id: 'diligence_agreements',  name: 'Diligence & Agreement',   color: '#8B5CF6' },
  { id: 'closed',                name: 'Closing',                 color: '#10B981' },
  { id: 'post_close_integration',name: 'Post Close Completed',    color: '#0EA5E9' },
  { id: 'complete',              name: 'Complete',                color: '#059669' },
];

const SPECIAL_STAGE_IDS = ['archived', 'discontinued'];

function Pipeline({ onNavigate, deals: propDeals = [], setDeals: setPropDeals, onOpenDeal, currentUser }) {
  const canEdit = can(currentUser, 'edit');
  const [deals, setDeals] = useState(propDeals);
  const [loading, setLoading] = useState(propDeals.length === 0);
  const [viewMode, setViewMode] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showSpecialView, setShowSpecialView] = useState(null);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editingStageName, setEditingStageName] = useState('');
  const [newStageName, setNewStageName] = useState('');
  const [showAddStage, setShowAddStage] = useState(false);

  const [stages, setStages] = useState(() => {
    try {
      const saved = localStorage.getItem('pipeline_stages');
      return saved ? JSON.parse(saved) : DEFAULT_STAGES;
    } catch {
      return DEFAULT_STAGES;
    }
  });

  // Sync with shared deals prop from App.js
  useEffect(() => {
    setDeals(propDeals);
    setLoading(false);
  }, [propDeals]);

  const saveStages = (newStages) => {
    setStages(newStages);
    localStorage.setItem('pipeline_stages', JSON.stringify(newStages));
  };

  const updateDealStage = async (dealId, newStageId) => {
    try {
      await dealsApi.update(dealId, { stage: newStageId });
      const updated = prev =>
        prev.map(d => d.id === dealId ? { ...d, stage: newStageId, updatedAt: new Date().toISOString() } : d);
      setDeals(updated);
      if (setPropDeals) setPropDeals(updated);
    } catch (error) {
      console.error('Failed to update deal stage:', error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setShowOwnerDropdown(false);
      setShowIndustryDropdown(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Derived state
  const activeStageIds = stages.map(s => s.id);
  const activeDeals = deals.filter(d => activeStageIds.includes(d.stage));
  const archivedDeals = deals.filter(d => d.stage === 'archived');
  const discontinuedDeals = deals.filter(d => d.stage === 'discontinued');
  const pipelineValue = activeDeals.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);

  const allOwners = [...new Set(deals.map(d => d.owner).filter(Boolean))];
  const allIndustries = [...new Set(deals.map(d => d.industry).filter(Boolean))];

  const filterDeals = (dealList) =>
    dealList.filter(deal => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        deal.name?.toLowerCase().includes(q) ||
        deal.industry?.toLowerCase().includes(q) ||
        deal.owner?.toLowerCase().includes(q);
      const matchesOwner = ownerFilter === 'all' || deal.owner === ownerFilter;
      const matchesIndustry = industryFilter === 'all' || deal.industry === industryFilter;
      return matchesSearch && matchesOwner && matchesIndustry;
    });

  const filteredActiveDeals = filterDeals(activeDeals);

  const getDealsForStage = (stageId) =>
    filteredActiveDeals.filter(d => d.stage === stageId);

  const getStageTotalValue = (stageId) =>
    getDealsForStage(stageId).reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);

  const getStageById = (stageId) =>
    stages.find(s => s.id === stageId) ||
    (stageId === 'archived' ? { id: 'archived', name: 'Archived', color: '#9CA3AF' } : null) ||
    (stageId === 'discontinued' ? { id: 'discontinued', name: 'Discontinued', color: '#EF4444' } : null) ||
    { id: stageId, name: stageId, color: '#6B7280' };

  const formatValue = (val) => {
    const num = parseFloat(val);
    if (!num) return null;
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(0)}M`;
    if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
    return `$${num}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return null;
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1d ago';
    return `${days}d ago`;
  };

  // Drag and drop
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (!canEdit) { setDraggedDeal(null); setDragOverStage(null); return; }
    if (draggedDeal && draggedDeal.stage !== stageId) {
      updateDealStage(draggedDeal.id, stageId);
    }
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  const handleDragEnd = () => {
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  // Customize functions
  const startEditStage = (stage) => {
    setEditingStageId(stage.id);
    setEditingStageName(stage.name);
  };

  const saveEditStage = () => {
    if (!editingStageName.trim()) return;
    saveStages(stages.map(s => s.id === editingStageId ? { ...s, name: editingStageName.trim() } : s));
    setEditingStageId(null);
    setEditingStageName('');
  };

  const deleteStage = (stageId) => {
    saveStages(stages.filter(s => s.id !== stageId));
  };

  const addStage = () => {
    if (!newStageName.trim()) return;
    const colors = ['#6B7280', '#F59E0B', '#3B82F6', '#8B5CF6', '#10B981', '#0EA5E9', '#EC4899', '#F97316'];
    const newStage = {
      id: `stage_${Date.now()}`,
      name: newStageName.trim(),
      color: colors[stages.length % colors.length],
    };
    saveStages([...stages, newStage]);
    setNewStageName('');
    setShowAddStage(false);
  };

  const specialViewDeals = showSpecialView === 'archived'
    ? filterDeals(archivedDeals)
    : showSpecialView === 'discontinued'
      ? filterDeals(discontinuedDeals)
      : [];

  if (loading) {
    return (
      <div className="pipeline-loading">
        <div className="pipeline-spinner" />
        <p>Loading pipeline...</p>
      </div>
    );
  }

  return (
    <div className="pipeline-page" onClick={() => { setShowOwnerDropdown(false); setShowIndustryDropdown(false); }}>
      {/* Header */}
      <div className="pipeline-header">
        <div className="pipeline-header-left">
          <h1 className="pipeline-title">Deal Pipeline</h1>
          <p className="pipeline-subtitle">System of record for all active deal opportunities and transactions.</p>
        </div>
        <div className="pipeline-header-right">
          <div className="pipeline-view-toggle">
            <button
              className={`pipeline-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={15} /> List View
            </button>
            <button
              className={`pipeline-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid size={15} /> Kanban View
            </button>
          </div>
          {canEdit && <button
            className="pipeline-customize-btn"
            style={{ backgroundColor: '#161f4a', color: '#fff', borderColor: '#161f4a' }}
            onClick={() => onNavigate && onNavigate('deals', { openCreate: true })}
          >
            <Plus size={15} /> Create Deal
          </button>}
          {canEdit && <button className="pipeline-customize-btn" onClick={() => setShowCustomize(true)}>
            <Settings size={15} /> Customize
          </button>}
        </div>
      </div>

      {/* Metrics Row */}
      <div className="pipeline-metrics">
        <div className="pipeline-metric-card">
          <div className="pipeline-metric-label">Active Deals</div>
          <div className="pipeline-metric-value">{activeDeals.length}</div>
        </div>
        <div className="pipeline-metric-card pipeline-metric-card--green">
          <div className="pipeline-metric-label">
            <DollarSign size={13} style={{ display: 'inline', marginRight: 2 }} />Pipeline Value
          </div>
          <div className="pipeline-metric-value pipeline-metric-value--green">
            {formatValue(pipelineValue) || '$0'}
          </div>
          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>* Single currency assumed</div>
        </div>
        <div
          className={`pipeline-metric-card pipeline-metric-card--clickable ${showSpecialView === 'archived' ? 'pipeline-metric-card--active' : ''}`}
          onClick={(e) => { e.stopPropagation(); setShowSpecialView(showSpecialView === 'archived' ? null : 'archived'); }}
        >
          <div className="pipeline-metric-label">
            <Archive size={13} style={{ display: 'inline', marginRight: 4 }} />Archive
          </div>
          <div className="pipeline-metric-value pipeline-metric-value--gray">{archivedDeals.length}</div>
          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>Closed &amp; completed deals</div>
        </div>
        <div
          className={`pipeline-metric-card pipeline-metric-card--clickable ${showSpecialView === 'discontinued' ? 'pipeline-metric-card--active' : ''}`}
          onClick={(e) => { e.stopPropagation(); setShowSpecialView(showSpecialView === 'discontinued' ? null : 'discontinued'); }}
        >
          <div className="pipeline-metric-label">
            <XCircle size={13} style={{ display: 'inline', marginRight: 4 }} />Terminated
          </div>
          <div className="pipeline-metric-value pipeline-metric-value--red">{discontinuedDeals.length}</div>
          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>Passed or discontinued deals</div>
        </div>
      </div>

      {/* Special view banner */}
      {showSpecialView && (
        <div className={`pipeline-special-banner pipeline-special-banner--${showSpecialView}`}>
          <span>
            {showSpecialView === 'archived' ? 'Archive — Closed & Completed Deals' : 'Terminated — Passed & Discontinued Deals'}
          </span>
          <button onClick={() => setShowSpecialView(null)}>
            <X size={14} /> Back to Pipeline
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="pipeline-filters">
        <div className="pipeline-search-box" onClick={e => e.stopPropagation()}>
          <Search size={15} className="pipeline-search-icon" />
          <input
            type="text"
            placeholder="Search deals..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Owner Filter */}
        <div className="pipeline-filter-dropdown" onClick={e => e.stopPropagation()}>
          <button
            className="pipeline-filter-btn"
            onClick={() => { setShowOwnerDropdown(v => !v); setShowIndustryDropdown(false); }}
          >
            {ownerFilter === 'all' ? 'All Owners' : ownerFilter}
            <ChevronDown size={14} />
          </button>
          {showOwnerDropdown && (
            <div className="pipeline-dropdown-menu">
              <button
                className={ownerFilter === 'all' ? 'selected' : ''}
                onClick={() => { setOwnerFilter('all'); setShowOwnerDropdown(false); }}
              >
                All Owners {ownerFilter === 'all' && '✓'}
              </button>
              {allOwners.map(o => (
                <button
                  key={o}
                  className={ownerFilter === o ? 'selected' : ''}
                  onClick={() => { setOwnerFilter(o); setShowOwnerDropdown(false); }}
                >
                  {o} {ownerFilter === o && '✓'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Industry Filter */}
        <div className="pipeline-filter-dropdown" onClick={e => e.stopPropagation()}>
          <button
            className="pipeline-filter-btn"
            onClick={() => { setShowIndustryDropdown(v => !v); setShowOwnerDropdown(false); }}
          >
            {industryFilter === 'all' ? 'All Industries' : industryFilter}
            <ChevronDown size={14} />
          </button>
          {showIndustryDropdown && (
            <div className="pipeline-dropdown-menu">
              <button
                className={industryFilter === 'all' ? 'selected' : ''}
                onClick={() => { setIndustryFilter('all'); setShowIndustryDropdown(false); }}
              >
                All Industries {industryFilter === 'all' && '✓'}
              </button>
              {allIndustries.map(i => (
                <button
                  key={i}
                  className={industryFilter === i ? 'selected' : ''}
                  onClick={() => { setIndustryFilter(i); setShowIndustryDropdown(false); }}
                >
                  {i} {industryFilter === i && '✓'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      {showSpecialView ? (
        <div className="pipeline-list-view">
          {specialViewDeals.length === 0 ? (
            <div className="pipeline-empty-state">
              <Building2 size={36} />
              <p>No {showSpecialView} deals.</p>
            </div>
          ) : (
            specialViewDeals.map(deal => (
              <ListDealRow
                key={deal.id}
                deal={deal}
                getStageById={getStageById}
                formatValue={formatValue}
                timeAgo={timeAgo}
                updateDealStage={updateDealStage}
                activeStages={stages}
                isSpecialView
                onOpenDeal={onOpenDeal}
                canEdit={canEdit}
              />
            ))
          )}
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="kanban-board">
          {stages.map(stage => {
            const stageDeals = getDealsForStage(stage.id);
            const stageValue = getStageTotalValue(stage.id);
            return (
              <div
                key={stage.id}
                className={`kanban-column ${dragOverStage === stage.id ? 'kanban-column--drag-over' : ''}`}
                onDragOver={e => handleDragOver(e, stage.id)}
                onDrop={e => handleDrop(e, stage.id)}
                onDragLeave={() => setDragOverStage(null)}
              >
                <div className="kanban-column-header">
                  <div className="kanban-column-title">
                    <span
                      className="kanban-stage-dot"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="kanban-stage-name">{stage.name}</span>
                  </div>
                  <span
                    className="kanban-count-badge"
                    style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
                  >
                    {stageDeals.length}
                  </span>
                </div>
                {stageValue > 0 && (
                  <div className="kanban-column-value">{formatValue(stageValue)}</div>
                )}
                <div className="kanban-cards">
                  {stageDeals.length === 0 ? (
                    <div className="kanban-empty">Drop deals here</div>
                  ) : (
                    stageDeals.map(deal => (
                      <KanbanCard
                        key={deal.id}
                        deal={deal}
                        stage={stage}
                        formatValue={formatValue}
                        formatDate={formatDate}
                        timeAgo={timeAgo}
                        onDragStart={e => handleDragStart(e, deal)}
                        onDragEnd={handleDragEnd}
                        stages={stages}
                        updateDealStage={updateDealStage}
                        onOpenDeal={onOpenDeal}
                        canEdit={canEdit}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}

          {/* Archive + Discontinued drop zones */}
          <div
            className={`kanban-column kanban-column--special ${dragOverStage === 'archived' ? 'kanban-column--drag-over' : ''}`}
            onDragOver={e => handleDragOver(e, 'archived')}
            onDrop={e => handleDrop(e, 'archived')}
            onDragLeave={() => setDragOverStage(null)}
          >
            <div className="kanban-column-header">
              <div className="kanban-column-title">
                <Archive size={14} style={{ color: '#9CA3AF' }} />
                <span className="kanban-stage-name">Archived</span>
              </div>
              <span className="kanban-count-badge" style={{ backgroundColor: '#9CA3AF20', color: '#9CA3AF' }}>
                {archivedDeals.length}
              </span>
            </div>
            <div className="kanban-special-hint">Drag deals here to archive</div>
            {archivedDeals.length > 0 && (
              <button className="kanban-view-special-btn" onClick={() => setShowSpecialView('archived')}>
                View {archivedDeals.length} archived
              </button>
            )}
          </div>

          <div
            className={`kanban-column kanban-column--special ${dragOverStage === 'discontinued' ? 'kanban-column--drag-over' : ''}`}
            onDragOver={e => handleDragOver(e, 'discontinued')}
            onDrop={e => handleDrop(e, 'discontinued')}
            onDragLeave={() => setDragOverStage(null)}
          >
            <div className="kanban-column-header">
              <div className="kanban-column-title">
                <XCircle size={14} style={{ color: '#EF4444' }} />
                <span className="kanban-stage-name">Discontinued</span>
              </div>
              <span className="kanban-count-badge" style={{ backgroundColor: '#EF444420', color: '#EF4444' }}>
                {discontinuedDeals.length}
              </span>
            </div>
            <div className="kanban-special-hint">Drag deals here to discontinue</div>
            {discontinuedDeals.length > 0 && (
              <button className="kanban-view-special-btn" onClick={() => setShowSpecialView('discontinued')}>
                View {discontinuedDeals.length} discontinued
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="pipeline-list-view">
          {filteredActiveDeals.length === 0 ? (
            <div className="pipeline-empty-state">
              <Building2 size={36} />
              <p>No active deals. Create a deal from the <strong>Deals</strong> page and it will appear here.</p>
            </div>
          ) : (
            filteredActiveDeals.map(deal => (
              <ListDealRow
                key={deal.id}
                deal={deal}
                getStageById={getStageById}
                formatValue={formatValue}
                timeAgo={timeAgo}
                updateDealStage={updateDealStage}
                activeStages={stages}
                onOpenDeal={onOpenDeal}
                canEdit={canEdit}
              />
            ))
          )}
        </div>
      )}

      {/* Customize Modal */}
      {showCustomize && (
        <div className="pipeline-modal-overlay" onClick={() => setShowCustomize(false)}>
          <div className="customize-modal" onClick={e => e.stopPropagation()}>
            <div className="customize-modal-header">
              <div>
                <h2>Customize Pipeline</h2>
                <p>Rename, add, or remove deal stages.</p>
              </div>
              <button className="customize-modal-close" onClick={() => setShowCustomize(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="customize-modal-body">
              <div className="customize-stages-list">
                {stages.map((stage) => (
                  <div key={stage.id} className="customize-stage-row">
                    <div className="stage-color-indicator" style={{ backgroundColor: stage.color }} />
                    {editingStageId === stage.id ? (
                      <div className="stage-edit-inline">
                        <input
                          autoFocus
                          value={editingStageName}
                          onChange={e => setEditingStageName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEditStage();
                            if (e.key === 'Escape') setEditingStageId(null);
                          }}
                          className="stage-name-input"
                        />
                        <button className="stage-btn stage-btn--save" onClick={saveEditStage}>
                          <Check size={13} />
                        </button>
                        <button className="stage-btn" onClick={() => setEditingStageId(null)}>
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="customize-stage-label">{stage.name}</span>
                        <div className="customize-stage-actions">
                          <button className="stage-btn" title="Rename" onClick={() => startEditStage(stage)}>
                            <Edit2 size={13} />
                          </button>
                          {stages.length > 1 && (
                            <button className="stage-btn stage-btn--danger" title="Delete" onClick={() => deleteStage(stage.id)}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Add stage */}
              {showAddStage ? (
                <div className="add-stage-form">
                  <input
                    autoFocus
                    placeholder="Stage name..."
                    value={newStageName}
                    onChange={e => setNewStageName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') addStage();
                      if (e.key === 'Escape') { setShowAddStage(false); setNewStageName(''); }
                    }}
                    className="stage-name-input"
                  />
                  <button className="stage-btn stage-btn--save" onClick={addStage}><Check size={13} /></button>
                  <button className="stage-btn" onClick={() => { setShowAddStage(false); setNewStageName(''); }}><X size={13} /></button>
                </div>
              ) : (
                <button className="add-stage-btn" onClick={() => setShowAddStage(true)}>
                  <Plus size={15} /> Add Stage
                </button>
              )}

              <div className="customize-modal-footer">
                <button
                  className="reset-defaults-btn"
                  onClick={() => { saveStages(DEFAULT_STAGES); }}
                >
                  Reset to defaults
                </button>
                <button className="pipeline-btn-primary" onClick={() => setShowCustomize(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KanbanCard({ deal, stage, formatValue, formatDate, timeAgo, onDragStart, onDragEnd, stages, updateDealStage, onOpenDeal, canEdit }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className="kanban-card"
      draggable={canEdit}
      onDragStart={canEdit ? onDragStart : undefined}
      onDragEnd={canEdit ? onDragEnd : undefined}
    >
      <div className="kanban-card-top">
        <button
          className="kanban-card-name kanban-card-name--link"
          onClick={e => { e.stopPropagation(); onOpenDeal?.(deal); }}
          title="Open deal"
        >
          {deal.name}
        </button>
        <div className="kanban-card-menu-wrapper">
          <button
            className="kanban-card-menu-btn"
            onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }}
          >
            ⋮
          </button>
          {showMenu && (
            <div className="kanban-card-menu-dropdown" onClick={e => e.stopPropagation()}>
              <button
                className="kanban-dropdown-item"
                onClick={() => { onOpenDeal?.(deal); setShowMenu(false); }}
              >
                <Edit2 size={12} /> Open / Edit Deal
              </button>
              {canEdit && <>
              <div className="kanban-dropdown-divider" />
              <div className="kanban-dropdown-section-label">Move to stage</div>
              {stages.map(s => (
                <button
                  key={s.id}
                  className={`kanban-dropdown-item ${deal.stage === s.id ? 'kanban-dropdown-item--current' : ''}`}
                  onClick={() => { updateDealStage(deal.id, s.id); setShowMenu(false); }}
                >
                  <span className="kanban-dropdown-dot" style={{ backgroundColor: s.color }} />
                  {s.name}
                </button>
              ))}
              <div className="kanban-dropdown-divider" />
              <button
                className="kanban-dropdown-item kanban-dropdown-item--danger"
                onClick={() => { updateDealStage(deal.id, 'archived'); setShowMenu(false); }}
              >
                <Archive size={12} /> Archive
              </button>
              <button
                className="kanban-dropdown-item kanban-dropdown-item--danger"
                onClick={() => { updateDealStage(deal.id, 'discontinued'); setShowMenu(false); }}
              >
                <XCircle size={12} /> Discontinue
              </button>
            </>}
            </div>
          )}
        </div>
      </div>

      {deal.value && (
        <div className="kanban-card-value">
          <DollarSign size={11} /> {formatValue(deal.value)}
        </div>
      )}
      {deal.targetClose && (
        <div className="kanban-card-date">
          <Calendar size={11} /> Target: {formatDate(deal.targetClose)}
        </div>
      )}
      {deal.industry && (
        <div className="kanban-card-tags">
          <span className="kanban-tag">{deal.industry}</span>
        </div>
      )}
      <div className="kanban-card-footer">
        {deal.owner && (
          <span className="kanban-card-owner">
            <User size={10} /> {deal.owner}
          </span>
        )}
        {deal.updatedAt && (
          <span className="kanban-card-time">
            <Clock size={10} /> {timeAgo(deal.updatedAt)}
          </span>
        )}
      </div>
    </div>
  );
}

function ListDealRow({ deal, getStageById, formatValue, timeAgo, updateDealStage, activeStages, isSpecialView, onOpenDeal, canEdit }) {
  const stage = getStageById(deal.stage);
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="pipeline-list-row" onClick={() => setShowMenu(false)}>
      <div className="pipeline-list-row-main">
        <button
          className="pipeline-list-deal-name pipeline-list-deal-name--link"
          onClick={e => { e.stopPropagation(); onOpenDeal?.(deal); }}
          title="Open deal"
        >
          {deal.name}
        </button>
        <div className="pipeline-list-deal-meta">
          {deal.industry && <span>{deal.industry}</span>}
          {deal.value && <span>• {formatValue(deal.value)}</span>}
          {deal.owner && <span>• Owner: {deal.owner}</span>}
        </div>
      </div>
      <div className="pipeline-list-row-right">
        <div className="pipeline-list-stage-wrapper" onClick={e => e.stopPropagation()}>
          <button
            className="pipeline-list-stage-badge"
            style={{
              backgroundColor: `${stage.color}18`,
              color: stage.color,
              border: `1px solid ${stage.color}35`
            }}
            onClick={() => canEdit && setShowMenu(v => !v)}
          >
            {stage.name} {canEdit && <ChevronDown size={11} />}
          </button>
          {showMenu && canEdit && (
            <div className="pipeline-list-stage-dropdown">
              {activeStages.map(s => (
                <button
                  key={s.id}
                  className={`kanban-dropdown-item ${deal.stage === s.id ? 'kanban-dropdown-item--current' : ''}`}
                  onClick={() => { updateDealStage(deal.id, s.id); setShowMenu(false); }}
                >
                  <span className="kanban-dropdown-dot" style={{ backgroundColor: s.color }} />
                  {s.name}
                </button>
              ))}
              {!isSpecialView && (
                <>
                  <div className="kanban-dropdown-divider" />
                  <button
                    className="kanban-dropdown-item kanban-dropdown-item--danger"
                    onClick={() => { updateDealStage(deal.id, 'archived'); setShowMenu(false); }}
                  >
                    <Archive size={12} /> Archive
                  </button>
                  <button
                    className="kanban-dropdown-item kanban-dropdown-item--danger"
                    onClick={() => { updateDealStage(deal.id, 'discontinued'); setShowMenu(false); }}
                  >
                    <XCircle size={12} /> Discontinue
                  </button>
                </>
              )}
              {isSpecialView && (
                <>
                  <div className="kanban-dropdown-divider" />
                  <div className="kanban-dropdown-section-label">Restore to stage</div>
                  {activeStages.map(s => (
                    <button
                      key={`restore-${s.id}`}
                      className="kanban-dropdown-item"
                      onClick={() => { updateDealStage(deal.id, s.id); setShowMenu(false); }}
                    >
                      <span className="kanban-dropdown-dot" style={{ backgroundColor: s.color }} />
                      {s.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        {deal.updatedAt && (
          <span className="pipeline-list-time">
            <Clock size={11} /> {timeAgo(deal.updatedAt)}
          </span>
        )}
      </div>
    </div>
  );
}

export default Pipeline;
